import { useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { AgreementListPanel } from 'AgreementListPackage/AgreementListPanelModule/components/AgreementListPanel';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IInitialOrder } from 'CorePackage/MainModule/interfaces/IInitialOrder';
import { getStorageItem, setStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { TableColumnSortEnum } from 'CorePackage/MainModule/enums/TableColumnSortEnum';
import { getSortOrderByPeriod } from 'CorePackage/MainModule/common/getSortOrderByPeriod';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';

import { initialAgreementListTableColumns } from '../constants/initialAgreementListTableColumns';
import { getAgreementListData, getAgreementListTotalCount } from '../selectors/agreementListSelectors';
import { IAgreementRaw } from '../interfaces/IAgreementRaw';
import { AgreementListTable } from './AgreementListTable';
import {
  agreementListGetData,
  agreementListGetDataNextPage,
  agreementListInitPage,
  agreementListSetOrder,
} from '../actions/agreementListActions';

function getInitialTableColumns(
  AgreementListOrder: string | undefined,
  selectedTableColumnsIds: Array<ITableColumn<IAgreementRaw>['id']> | undefined,
) {
  const initialOrderString = AgreementListOrder;
  const initialOrder: IInitialOrder[] =
    initialOrderString?.split(';').map((orderStringItem: string) => {
      const [id, value] = orderStringItem.split(',');

      return {
        id,
        value,
      } as IInitialOrder;
    }) || [];

  const currentInitialTableColumns = initialAgreementListTableColumns;

  const initialTableColumns: ITableColumn<IAgreementRaw>[] = currentInitialTableColumns.map(initialTableColumn => ({
    ...initialTableColumn,
    sort: initialOrderString
      ? initialOrder.find(orderItem => orderItem.id === initialTableColumn.id)?.value || TableColumnSortEnum.NONE
      : initialTableColumn.sort,
    isSelected: selectedTableColumnsIds
      ? selectedTableColumnsIds.some(selectedTableColumnId => selectedTableColumnId === initialTableColumn.id)
      : initialTableColumn.isSelected,
  }));

  return initialTableColumns;
}

interface IAgreementListProps {
  agreements: IAgreementRaw[];
  totalCount: number;
}

const AgreementListWithoutStore = ({ agreements, totalCount }: IAgreementListProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const AgreementListOrder = getStorageItem<string>(StorageNamesEnum.AGREEMENT_LIST_ORDER);
  const selectedTableColumnsIds = getStorageItem<Array<ITableColumn<IAgreementRaw>['id']>>(
    StorageNamesEnum.AGREEMENT_LIST_TABLE_COLUMNS,
  );
  const [tableColumns, setTableColumns] = useState<ITableColumn<IAgreementRaw>[]>(
    getInitialTableColumns(AgreementListOrder, selectedTableColumnsIds),
  );

  useEffect(() => {
    const order = getSortOrder(
      getInitialTableColumns(AgreementListOrder, selectedTableColumnsIds),
      getSortOrderByPeriod,
    );

    dispatch(agreementListSetOrder(order));
  }, [dispatch, AgreementListOrder, selectedTableColumnsIds]);

  useEffect(() => {
    dispatch(agreementListInitPage());
  }, [dispatch]);

  const handleLoadClick = useCallback(() => dispatch(agreementListGetDataNextPage()), [dispatch]);

  useEffect(() => {
    setTableColumns(getInitialTableColumns(AgreementListOrder, selectedTableColumnsIds));
  }, []);

  const order = getSortOrder(tableColumns, getSortOrderByPeriod);

  useEffect(() => {
    setStorageItem(StorageNamesEnum.AGREEMENT_LIST_ORDER, order);
  }, [order]);

  useEffect(() => {
    const newSelectedTableColumnsIds = tableColumns.reduce(
      (tableColumnsIds: Array<ITableColumn<IAgreementRaw>['id']>, tableColumn) => {
        if (tableColumn?.isSelected !== false) {
          tableColumnsIds.push(tableColumn.id);
        }

        return tableColumnsIds;
      },
      [],
    );

    setStorageItem(StorageNamesEnum.AGREEMENT_LIST_TABLE_COLUMNS, newSelectedTableColumnsIds);
  }, [tableColumns]);

  const onChangeSort = useCallback(
    (newColumnsData: ITableColumn<IAgreementRaw>[]) => {
      const newOrder = getSortOrder(newColumnsData, getSortOrderByPeriod);

      dispatch(agreementListSetOrder(newOrder));
      dispatch(agreementListGetData());
    },
    [dispatch],
  );

  return (
    <TablePage>
      <AgreementListPanel dataCount={totalCount} />
      <AgreementListTable
        tableColumns={tableColumns}
        agreements={agreements}
        totalCount={totalCount}
        setTableColumns={setTableColumns}
        handleLoadClick={handleLoadClick}
        onChangeSort={onChangeSort}
      />
    </TablePage>
  );
};

const mapStateToProps = (state: IRootState) => ({
  agreements: getAgreementListData(state),
  totalCount: getAgreementListTotalCount(state),
});

export const AgreementList = connect(mapStateToProps)(AgreementListWithoutStore);
